// --------------------------------------------------------------
// Created On: 2023-06-23
// Author: Zachary Thomas
//
// Last Modified: 2026-08-28
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useParams } from "react-router-dom";
import {
  PRODUCT_TYPE_PUMP,
  PRODUCT_TYPE_LIFT_STATION,
  PRODUCT_TYPE_FLOW_SKID,
  PRODUCT_TYPE_STAND_ALONE_UNIT,
  PRODUCT_TYPE_MOTOR,
  PRODUCT_TYPE_FILTER_STATION,
} from "../../../constants/products";
import {
  SAMPLING_SUPPORTED_MONITORING_DEVICES,
  VALIDATION_SUPPORTED_MONITORING_DEVICES,
} from "src/constants/miscellaneous";
import CentrifugalPumpVisual from "./CentrifugalPumpVisual/CentrifugalPumpVisual";
import LiftStationVisual from "./LiftStationVisual/LiftStationVisual";
import FlowSkidVisual from "./FlowSkidVisual/FlowSkidVisual";
import StandAloneUnitVisual from "./StandAloneUnitVisual/StandAloneUnitVisual";
import MotorVisual from "./MotorVisual/MotorVisual";
import FilterStationVisual from "./FilterStationVisual/FilterStationVisual";
import ProductDataTable from "./ProductDataTable/ProductDataTable";
import AssetDataButtons from "../../../components/AssetDataButtons/AssetDataButtons";
import AssetDataMobileViewBanner from "./AssetDataMobileViewBanner/AssetDataMobileViewBanner";
import styles from "./ProductDataVisual.module.scss";

// A data visualization for a specific type of product.
export default function ProductDataVisual(props: Props): Component {
  const extraTallProductType = props.productType.includes(PRODUCT_TYPE_LIFT_STATION);
  const [attributeHoverList, setAttributeHoverList] = useState<boolean[]>([]);
  const params = useParams();
  const assetId = params.assetId ?? "";

  const gatewayAsset = useMemo(() => getGatewayAsset(props.networkAssets), [JSON.stringify(props.networkAssets)]);
  const deviceCanSample = useMemo(
    () => getDeviceCanSample(props.deviceType, assetId, gatewayAsset),
    [props.deviceType, assetId, JSON.stringify(gatewayAsset)]
  );
  const deviceCanValidate = useMemo(
    () => getDeviceCanValidate(props.deviceType, assetId, gatewayAsset),
    [props.deviceType, assetId, JSON.stringify(gatewayAsset)]
  );

  // Returns network asset information about the gateway asset.
  function getGatewayAsset(networkAssets: NetworkAsset[] | undefined): NetworkAsset | null {
    let gatewayAsset: NetworkAsset | null | undefined;
    if (networkAssets !== undefined) {
      gatewayAsset = networkAssets.find((networkAsset) => networkAsset.isGateway);
    }
    if (gatewayAsset === undefined) {
      gatewayAsset = null;
    }
    return gatewayAsset;
  }

  // Returns if the current device supports sampling.
  function getDeviceCanSample(
    deviceType: string | null,
    assetId: string | undefined,
    gatewayAsset: NetworkAsset | null
  ): boolean {
    const assetMonitoringDeviceCanSample =
      SAMPLING_SUPPORTED_MONITORING_DEVICES.includes(deviceType || "") && assetId !== null && assetId !== undefined;
    const networkMonitoringDeviceCanSample =
      gatewayAsset !== null &&
      SAMPLING_SUPPORTED_MONITORING_DEVICES.includes(gatewayAsset.networkDeviceTypeModel || "");
    return assetMonitoringDeviceCanSample || networkMonitoringDeviceCanSample;
  }

  // Returns if the current device supports validation.
  function getDeviceCanValidate(
    deviceType: string | null,
    assetId: string | undefined,
    gatewayAsset: NetworkAsset | null
  ): boolean {
    const assetMonitoringDeviceCanValidate =
      VALIDATION_SUPPORTED_MONITORING_DEVICES.includes(deviceType || "") && assetId !== null && assetId !== undefined;
    const networkMonitoringDeviceCanValidate =
      gatewayAsset !== null &&
      VALIDATION_SUPPORTED_MONITORING_DEVICES.includes(gatewayAsset.networkDeviceTypeModel || "");
    return assetMonitoringDeviceCanValidate || networkMonitoringDeviceCanValidate;
  }

  // See if the current product type should be displayed.
  function productTypeIsDisplayable(productType: string): boolean {
    return [
      PRODUCT_TYPE_PUMP,
      PRODUCT_TYPE_LIFT_STATION,
      PRODUCT_TYPE_FLOW_SKID,
      PRODUCT_TYPE_STAND_ALONE_UNIT,
      PRODUCT_TYPE_MOTOR,
      PRODUCT_TYPE_FILTER_STATION,
    ].includes(productType);
  }

  // Initialize the attribute hover list to show no attributes being hovered over.
  useEffect(() => {
    setAttributeHoverList(props.highlightAttributes.map(() => false));
  }, [JSON.stringify(props.highlightAttributes)]);

  // Set the current hover status of an attribute in the hover list.
  function updateHoverList(index: number, isHovering: boolean): void {
    setAttributeHoverList((prev) =>
      prev.map((hoverValue, i) => {
        if (i === index) {
          return isHovering;
        } else return hoverValue;
      })
    );
  }

  // Get the current attribute being hovered over, if any.
  function getHoverAttributeCode(): string | null {
    if (props.highlightAttributes.length === attributeHoverList.length) {
      const hoverIndex = attributeHoverList.findIndex((isHovering) => isHovering);
      if (hoverIndex > -1) {
        return props.highlightAttributes[hoverIndex].attributeCode;
      }
    }
    return null;
  }

  return (
    <div data-test="product-data-visual-container" className="my-3 py-3">
      <div className="row justify-content-center mx-0">
        <div className="col-12 col-xl-8">
          <div className={styles.container}>
            {props.assetName.length > 0 && (
              <Fragment>
                <div className={`${styles.header} d-flex justify-content-between align-items-center`}>
                  <div className="d-flex justify-content-start pt-2 align-items-center">
                    <div className="mx-auto pb-1 px-2">{props.assetName}</div>
                  </div>
                  <AssetDataButtons
                    assetId={parseInt(assetId)}
                    deviceId={props.deviceId}
                    deviceType={props.deviceType}
                    networkAssets={props.networkAssets}
                    allAttributes={props.allAttributes}
                    deviceCanValidate={deviceCanValidate}
                    deviceCanSample={deviceCanSample}
                    allowAssetValidate={props.allowAssetValidate}
                    allowDeviceSample={props.allowDeviceSample}
                    onClose={() => props.onEdit()}
                  />
                </div>
                {/* When the screen size is small or the user is viewing this page
                  through a mobile device, and the asset is attached to a device with
                  either sample or validation functionality, this component will appear
                  below the asset name title bar to save space. */}
                {((deviceCanSample && props.allowDeviceSample) ||
                  (deviceCanValidate && props.allowAssetValidate)) && (
                  <AssetDataMobileViewBanner
                    assetId={parseInt(assetId)}
                    deviceId={props.deviceId || 0}
                    deviceType={props.deviceType}
                    deviceCanValidate={deviceCanValidate}
                    deviceCanSample={deviceCanSample}
                    allowAssetValidate={props.allowAssetValidate}
                    allowDeviceSample={props.allowDeviceSample}
                  />
                )}
              </Fragment>
            )}
            <div className="row g-0">
              <div
                className={`${styles.body} ${extraTallProductType ? styles.large : ""} col-12 ${
                  props.highlightAttributes.length > 0 ? "col-lg-6" : "col-lg-12"
                }`}
              >
                {props.productType === PRODUCT_TYPE_PUMP && (
                  <CentrifugalPumpVisual attributes={props.allAttributes} hoverCode={getHoverAttributeCode()} />
                )}
                {props.productType === PRODUCT_TYPE_LIFT_STATION && (
                  <LiftStationVisual attributes={props.allAttributes} hoverCode={getHoverAttributeCode()} />
                )}
                {props.productType === PRODUCT_TYPE_FLOW_SKID && <FlowSkidVisual />}
                {props.productType === PRODUCT_TYPE_STAND_ALONE_UNIT && <StandAloneUnitVisual />}
                {props.productType === PRODUCT_TYPE_MOTOR && <MotorVisual />}
                {props.productType === PRODUCT_TYPE_FILTER_STATION && <FilterStationVisual />}
                {props.productType !== null &&
                  props.productType !== "" &&
                  props.productType !== undefined &&
                  !productTypeIsDisplayable(props.productType) && (
                    <div className="row justify-content-center py-4">
                      <i className={`${styles.noImage} fa fa-fw fa-question-circle-o`} />
                      <div className={styles.noImageLabel}>No product image available.</div>
                    </div>
                  )}
              </div>

              {props.highlightAttributes.length > 0 && (
                <ProductDataTable
                  attributes={props.highlightAttributes}
                  onUpdateHoverList={(index, isHovering) => updateHoverList(index, isHovering)}
                  onSelectAttribute={(attributeId) => props.onSelectAttribute(attributeId)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  deviceId: number | null;
  deviceType: string;
  assetName: string;
  productType: string;
  highlightAttributes: Attribute[];
  allAttributes: Attribute[];
  networkAssets: NetworkAsset[];
  allowDeviceSample: boolean;
  allowAssetValidate: boolean;
  onSelectAttribute: (attributeId: number) => void;
  onEdit: () => void;
}

interface Attribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  connectedRegAttributeId: number | null;
  unitShortName: string | null;
  unitLongName: string | null;
  icon: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  isHistorical: boolean;
  isGraphable: boolean;
  isStale: boolean;
}

interface NetworkAsset {
  assetId: number;
  assetName: string;
  isGateway: boolean;
  networkDeviceTypeModel: string;
  networkDeviceTypeName: string;
  networkDeviceIdentifier: string;
  userHasAccess: boolean;
}
